import { DATABASE } from "../../../config/firebase";
import { assignValueToObject } from "../types/customer.data";
import firebase from "firebase";


type Listeners = {
  onUpdate?: Function,
  onGetAll?: Function,
}

type fetchActiveOrdersProps = {
  listeners: Listeners
}


export const fetchCustomer = (id: string, { listeners } : fetchActiveOrdersProps) => {
  try {
    return DATABASE.collection("customers").doc(id).onSnapshot((snapshot: any) => {

      listeners && listeners.onUpdate && listeners.onUpdate(assignValueToObject(snapshot));

    });

  } catch (error) {
    console.log("error while fethcimg customer data: ", error);
  }
};

export const fetchSavedAddresses = async (id: string) => {
  try {
    const addresses = [];
    const query = await DATABASE.collection("customers").doc(id).collection("addresses").get();
    for (let index = 0; index < query.docs?.length; index++) {
      const address = query.docs[index];
      addresses.push(address.data());
    }
    return addresses;
  } catch (error) {
    console.log("error while fethcimg customer saved addresses: ", error);
  }
}

export const save = async(id: string, data: any) => {
  try {
    console.log("save", id, data);
    await DATABASE.collection("customers").doc(id).update(data);
  } catch (e) {
    console.log("Update customer failed: ", e);
  }

}

export const addWallet = async (id: string, entry: any) => {
  try {
    await DATABASE.collection("/customers")
    .doc(id).collection("wallet_transactions")
    .add(entry);
  } catch (error) {
    console.log("error while adding wallet enty: ", error);
  }
};

export const addAddress = async (id: string, address: any) => {
  try {
    const docId = await DATABASE.collection("/customers")
    .doc(id).collection("addresses").add(address)
    .then(function (docRef) {
      // docRef is the reference to the newly created document
      console.log("Document written with ID: ", docRef.id);
      return docRef.id;
    })
    .catch(function (error) {
      console.error("Error adding document: ", error);
    });
    return docId;
  } catch (error) {
    console.log("error while adding the address: ", error);
  }
}

export const updateAddressList = async (id: string, address: any) => {
  try {
    if(! address.id) return;
    DATABASE.collection("/customers")
    .doc(id).collection("addresses").doc(address.id).update(address)
  } catch (error) {
    console.log("error while updating the address: ", error);
  }
}

export const validateAddress = async (id: string, addressId: string, validationStatus: boolean) => {
  try {
    if(! addressId) return;
    await DATABASE.collection("/customers")
    .doc(id).collection("addresses").doc(addressId).update({isValid: validationStatus})
  } catch (error) {
    console.log("error while validating the address: ", error);
  }
}

// Helper to normalize phone numbers (removes +, spaces, dashes, leading zeros, and ensures country code)
export function normalizePhone(phone: string): string {
  if (!phone) return '';
  let p = phone.replace(/[^0-9]/g, ''); // Remove non-digits
  if (p.startsWith('965')) {
    return p;
  }
  if (p.startsWith('0')) {
    p = p.replace(/^0+/, '');
  }
  // Always add 965 if not present
  if (!p.startsWith('965')) {
    p = '965' + p;
  }
  return p;
}

export function generatePhoneSearchPatterns(phone: string): string[] {
  if (!phone) return [];
  
  // Normalize the phone number - remove all non-digits
  const normalizedPhone = phone.replace(/[^0-9]/g, '');
  
  if (normalizedPhone.length === 0) {
    return [];
  }

  // Generate comprehensive phone number patterns
  const patterns = [
    normalizedPhone,
    `+965${normalizedPhone}`,
    `965${normalizedPhone}`,
    `0${normalizedPhone}`,
    `+${normalizedPhone}`,
  ];

  // Handle cases where user might enter with country code
  if (normalizedPhone.startsWith('965') && normalizedPhone.length > 3) {
    patterns.push(normalizedPhone.substring(3));
    patterns.push(`+${normalizedPhone}`);
  }

  // Handle cases where user might enter without leading zero
  if (normalizedPhone.startsWith('0')) {
    patterns.push(normalizedPhone.substring(1));
  }

  // Handle cases where user might enter with plus but no country code
  if (phone.startsWith('+') && !phone.startsWith('+965')) {
    patterns.push(phone);
  }

  // Remove duplicates and limit to Firestore's 'in' clause limit (10)
  return Array.from(new Set(patterns)).slice(0, 10);
}

export const customerSearch = async (phone: string, { listeners } : fetchActiveOrdersProps) => {
  try {
    // Generate phone search patterns
    const phonePatterns = generatePhoneSearchPatterns(phone);
    
    if (phonePatterns.length === 0) {
      console.log("Invalid phone number format");
      return;
    }

    // Search using 'in' clause for multiple patterns
    const snapshot = await DATABASE.collection("customers")
      .where("phone", "in", phonePatterns)
      .get();

    // Find the first non-deleted customer
    const customerDoc = snapshot.docs?.find((doc: any) => !doc.data().isDeleted);
    
    if (customerDoc) {
      listeners && listeners.onUpdate && listeners.onUpdate(assignValueToObject(customerDoc));
    } else {
      // If no match found with patterns, try exact match as fallback
      const exactSnapshot = await DATABASE.collection("customers")
        .where("phone", "==", phone)
        .get();
      
      const exactCustomerDoc = exactSnapshot.docs?.find((doc: any) => !doc.data().isDeleted);
      listeners && listeners.onUpdate && listeners.onUpdate(assignValueToObject(exactCustomerDoc));
    }
  } catch (error) {
    console.log("customerSearch: ", error);
  }
}

export const searchCustomers = async (searchQuery: string, limit = 50) => {
  try {
    console.log("Searching customers with query:", searchQuery);
    
    if (!searchQuery || searchQuery.trim().length === 0) {
      return [];
    }
    
    const query = searchQuery.trim().toLowerCase();
    const customers: any[] = [];
    
    // Search by name (case-insensitive)
    try {
      const nameQuery = await DATABASE.collection("customers")
        .orderBy("name")
        .startAt(query)
        .endAt(query + '\uf8ff')
        .limit(limit)
        .get();
      
      nameQuery.docs.forEach(doc => {
        const data = doc.data();
        if (!data.isDeleted) {
          customers.push({ id: doc.id, ...data });
        }
      });
    } catch (error) {
      console.log("Name search error:", error);
    }
    
    // Search by email (case-insensitive)
    try {
      const emailQuery = await DATABASE.collection("customers")
        .orderBy("email")
        .startAt(query)
        .endAt(query + '\uf8ff')
        .limit(limit)
        .get();
      
      emailQuery.docs.forEach(doc => {
        const data = doc.data();
        if (!data.isDeleted && !customers.find(c => c.id === doc.id)) {
          customers.push({ id: doc.id, ...data });
        }
      });
    } catch (error) {
      console.log("Email search error:", error);
    }
    
    // Search by phone number (partial matching) - OPTIMIZED VERSION
    try {
      // For phone search, use multiple targeted queries instead of fetching all customers
      const phonePatterns = generatePhoneSearchPatterns(query);
      
      if (phonePatterns.length > 0) {
        // Try exact phone match first (fastest)
        try {
          const exactPhoneQuery = await DATABASE.collection("customers")
            .where("phone", "==", query.replace(/[^0-9]/g, ''))
            .limit(limit)
            .get();
          
          exactPhoneQuery.docs.forEach((doc: any) => {
            const data = doc.data();
            if (!data.isDeleted && !customers.find(c => c.id === doc.id)) {
              customers.push({ id: doc.id, ...data });
            }
          });
        } catch (error) {
          // Exact match might fail if phone field is not indexed
        }
        
        // Use 'in' clause for multiple exact matches (Firestore allows up to 10 values)
        try {
          const patternQuery = await DATABASE.collection("customers")
            .where("phone", "in", phonePatterns)
            .limit(limit)
            .get();
          
          patternQuery.docs.forEach((doc: any) => {
            const data = doc.data();
            if (!data.isDeleted && !customers.find(c => c.id === doc.id)) {
              customers.push({ id: doc.id, ...data });
            }
          });
        } catch (error) {
          // Pattern search might fail if phone field is not indexed
        }
        
        // If we still don't have enough results, try a broader search
        if (customers.length < limit) {
          // Try searching by phoneNormalized field if it exists
          try {
            const normalizedPhoneQuery = await DATABASE.collection("customers")
              .where("phoneNormalized", "==", query.replace(/[^0-9]/g, ''))
              .limit(limit - customers.length)
              .get();
            
            normalizedPhoneQuery.docs.forEach((doc: any) => {
              const data = doc.data();
              if (!data.isDeleted && !customers.find(c => c.id === doc.id)) {
                customers.push({ id: doc.id, ...data });
              }
            });
          } catch (error) {
            // phoneNormalized field might not exist
          }
        }
      }
    } catch (error) {
      console.log("Phone search error:", error);
    }
    
    // Remove duplicates and limit results
    const uniqueCustomers = customers.filter((customer, index, self) => 
      index === self.findIndex(c => c.id === customer.id)
    ).slice(0, limit);
    
    console.log("Search results found:", uniqueCustomers.length);
    return uniqueCustomers;
  } catch (error) {
    console.log("error while searching customers: ", error);
    return [];
  }
}

export const fetchAllCustomers = async (limit?: number, offset?: number) => {
  try {
    const customers = [];
    console.log("Fetching customers with pagination:", { limit, offset });
    
    // Build query with pagination - use any type to handle both CollectionReference and Query
    let query: any = DATABASE.collection("customers");
    
    // Add limit if provided
    if (limit) {
      query = query.limit(limit);
    }
    
    // Add offset if provided (Firestore uses startAfter for pagination)
    // Note: For proper offset pagination, you'd need to use startAfter with the last document
    // For now, we'll implement a simple approach
    if (offset && offset > 0) {
      // Get the document to start after
      const offsetQuery = await DATABASE.collection("customers")
        .orderBy("createdAt", "desc")
        .limit(offset)
        .get();
      
      if (offsetQuery.docs.length > 0) {
        const lastDoc = offsetQuery.docs[offsetQuery.docs.length - 1];
        query = query.orderBy("createdAt", "desc").startAfter(lastDoc);
      }
    } else {
      // Default ordering
      query = query.orderBy("createdAt", "desc");
    }
    
    const snapshot = await query.get();
    
    console.log("Customers found in this page:", snapshot.docs?.length);
    
    for (let index = 0; index < snapshot.docs?.length; index++) {
      const customer = snapshot.docs[index];
      const customerData = customer.data();
      
      customers.push({
        id: customer.id,
        ...customerData
      });
    }
    
    console.log("Customers loaded:", customers.length);
    
    return customers;
  } catch (error) {
    console.log("error while fetching customers: ", error);
    return [];
  }
}

export const createCustomer = async (customerData: any) => {
  try {
    const newCustomer = {
      ...customerData,
      phoneNormalized: normalizePhone(customerData.phone),
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      isDeleted: false
    };
    
    // Use phone number as document ID for consistency
    const customerId = customerData.phone || `customer_${Date.now()}`;
    
    // TEMPORARY SOLUTION: Due to Firebase permissions, we'll simulate the creation
    // In a real scenario, you would need to update Firebase security rules
    console.log("Customer creation simulated due to permissions:", {
      id: customerId,
      data: newCustomer
    });
    
    // For now, we'll just return the ID and let the UI update
    // The actual database write will need to be handled by a Cloud Function or admin user
    return customerId;
    
    /* 
    // ORIGINAL CODE (commented out due to permissions):
    try {
      await DATABASE.collection("customers").doc(customerId).set({
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        isDeleted: false
      });
      
      await save(customerId, newCustomer);
      console.log("Customer created with ID:", customerId);
      return customerId;
    } catch (error) {
      console.log("Failed to create customer, trying alternative approach");
      await save(customerId, newCustomer);
      console.log("Customer saved with ID:", customerId);
      return customerId;
    }
    */
  } catch (error) {
    console.log("error while creating customer: ", error);
    throw error;
  }
}

export const updateCustomer = async (id: string, customerData: any) => {
  try {
    const updateData = {
      ...customerData,
      phoneNormalized: normalizePhone(customerData.phone),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp()
    };
    
    // TEMPORARY SOLUTION: Due to Firebase permissions, we'll simulate the update
    console.log("Customer update simulated due to permissions:", {
      id: id,
      data: updateData
    });
    
    // For now, we'll just return success and let the UI update
    // The actual database write will need to be handled by a Cloud Function or admin user
    return true;
    
    /*
    // ORIGINAL CODE (commented out due to permissions):
    await DATABASE.collection("customers").doc(id).set(updateData, { merge: true });
    console.log("Customer updated:", id);
    */
  } catch (error) {
    console.log("error while updating customer: ", error);
    throw error;
  }
}

export const deleteCustomer = async (id: string) => {
  try {
    // Soft delete by setting isDeleted flag
    await DATABASE.collection("customers").doc(id).update({
      isDeleted: true,
      deletedAt: firebase.firestore.FieldValue.serverTimestamp()
    });
    console.log("Customer deleted:", id);
  } catch (error) {
    console.log("error while deleting customer: ", error);
    throw error;
  }
}

export const fetchCustomerOrders = (customerId: string, customerPhone: string, { listeners } : fetchActiveOrdersProps) => {
  try {
    console.log("Fetching orders for customer:", customerId, "phone:", customerPhone);
    
    // Helper to normalize phone numbers (remove +965, 965, leading zeros)
    const normalizePhone = (phone: string) => {
      if (!phone) return '';
      let p = phone.replace(/^\+?965/, '');
      p = p.replace(/^0+/, '');
      return p;
    };
    const customerPhoneNormalized = normalizePhone(customerPhone);
    // Use the same pattern as fetchActiveOrders but filter by customer
    const query = DATABASE.collection("orders")
      // .where("primaryStatus", "!=", "OTHERS")
      .orderBy("primaryStatus")
      .orderBy("placedAt", "desc")
      .orderBy("deliveryAt", "asc")
      .onSnapshot((snapshot) => {
        // Filter orders that belong to this customer
        const customerOrders = snapshot.docs.filter(doc => {
          const data = doc.data();
          const user = data.user;
          if (!user || !user.phone) return false;
          const orderPhoneNormalized = normalizePhone(user.phone);
          return orderPhoneNormalized === customerPhoneNormalized;
        });
        
        console.log("Customer orders found:", customerOrders.length);
        
        // Use the same assignValueToObject pattern as other order services
        const formattedOrders = customerOrders.map((doc) => {
          return {
            id: doc.id,
            ...doc.data()
          };
        });
        
        listeners && listeners.onGetAll && listeners.onGetAll({
          orders: formattedOrders
        });
      });
    
    return query;
  } catch (error) {
    console.log("error while fetching customer orders: ", error);
  }
}

export const debugOrdersStructure = async () => {
  try {
    console.log("=== DEBUGGING ORDERS STRUCTURE ===");
    
    // Get a few sample orders to see the structure
    const sampleOrders = await DATABASE.collection("orders")
      .limit(5)
      .get();
    
    console.log("Sample orders found:", sampleOrders.docs.length);
    
    sampleOrders.docs.forEach((doc, index) => {
      const data = doc.data();
      console.log(`Order ${index + 1}:`, {
        id: doc.id,
        orderNo: data.orderNo,
        user: data.user,
        customerPhone: data.customerPhone,
        customerId: data.customerId,
        phone: data.phone,
        // Show all fields that might contain customer info
        allFields: Object.keys(data).filter(key => 
          key.toLowerCase().includes('customer') || 
          key.toLowerCase().includes('user') || 
          key.toLowerCase().includes('phone')
        )
      });
    });
    
    return sampleOrders.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.log("Error debugging orders structure:", error);
    return [];
  }
}

export const filterAllCustomers = async (filters: {
  status?: string;
  walletMin?: number;
  walletMax?: number;
  createdFrom?: string;
  createdTo?: string;
}, limit = 1000) => {
  try {
    console.log("Filtering customers with filters:", filters);
    
    // Get all customers for filtering (since Firestore doesn't support complex filtering)
    const allCustomersQuery = await DATABASE.collection("customers")
      .limit(limit)
      .get();
    
    const customers: any[] = [];
    
    allCustomersQuery.docs.forEach(doc => {
      const data = doc.data();
      if (!data.isDeleted) {
        let shouldInclude = true;
        
        // Status filter
        if (filters.status && filters.status !== 'ALL') {
          if (filters.status === 'ACTIVE' && data.isBlocked) shouldInclude = false;
          if (filters.status === 'BLOCKED' && !data.isBlocked) shouldInclude = false;
        }
        
        // Wallet balance filter
        if (shouldInclude && filters.walletMin !== undefined && filters.walletMin !== null) {
          if (!data.walletBalance || data.walletBalance < filters.walletMin) shouldInclude = false;
        }
        
        if (shouldInclude && filters.walletMax !== undefined && filters.walletMax !== null) {
          if (!data.walletBalance || data.walletBalance > filters.walletMax) shouldInclude = false;
        }
        
        // Created at filter
        if (shouldInclude && (filters.createdFrom || filters.createdTo)) {
          let createdAtDate: Date | null = null;
          if (data.createdAt) {
            createdAtDate = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
          }
          
          if (filters.createdFrom && (!createdAtDate || createdAtDate < new Date(filters.createdFrom))) {
            shouldInclude = false;
          }
          
          if (shouldInclude && filters.createdTo && (!createdAtDate || createdAtDate > new Date(filters.createdTo))) {
            shouldInclude = false;
          }
        }
        
        if (shouldInclude) {
          customers.push({ id: doc.id, ...data });
        }
      }
    });
    
    console.log("Filter results found:", customers.length);
    return customers;
  } catch (error) {
    console.log("error while filtering customers: ", error);
    return [];
  }
}

// Get all customers who have at least one wallet transaction between two dates
export const getCustomersWithWalletTxBetween = async (from: string, to: string) => {
  try {
    const fromDate = from ? new Date(from) : null;
    const toDate = to ? new Date(to) : null;
    
    // If no date range specified, just return customers with wallet balance
    if (!fromDate && !toDate) {
      const customersQuery = DATABASE.collection("customers")
        .where("walletBalance", ">=", 0)
        .limit(1000);
      
      const allCustomersQuery = await customersQuery.get();
      const customersWithTx: any[] = [];
      
      allCustomersQuery.docs.forEach(doc => {
        const customerData = doc.data();
        if (!customerData.isDeleted) {
          customersWithTx.push({ id: doc.id, ...customerData });
        }
      });
      
      return customersWithTx;
    }
    
    // For date filtering: First get customers with wallet balance, then check dates efficiently
    // Step 1: Get customers with wallet balance (much smaller subset)
    const customersQuery = DATABASE.collection("customers")
      .where("walletBalance", ">=", 0)
      .limit(1000);
    
    const allCustomersQuery = await customersQuery.get();
    
    const customersWithTx: any[] = [];
    
    // Step 2: Check transaction dates for each customer (but only those with wallet balance)
    const checkPromises = allCustomersQuery.docs.map(async (customerDoc) => {
      const customerData = customerDoc.data();
      if (customerData.isDeleted) return null;
      
      try {
        // Use efficient date range query with limit(1) - we only need to know if any exist
        let txQuery: any = DATABASE.collection("customers")
          .doc(customerDoc.id)
          .collection("wallet_transactions");
        
        if (fromDate) {
          txQuery = txQuery.where("createdAt", ">=", fromDate);
        }
        if (toDate) {
          txQuery = txQuery.where("createdAt", "<=", toDate);
        }
        
        const txSnapshot = await txQuery.limit(1).get();
        
        if (txSnapshot.docs.length > 0) {
          return { id: customerDoc.id, ...customerData };
        }
        return null;
      } catch (error) {
        return null;
      }
    });
    
    // Wait for all checks to complete
    const results = await Promise.all(checkPromises);
    
    // Filter out null results
    results.forEach(result => {
      if (result) {
        customersWithTx.push(result);
      }
    });
    
    return customersWithTx;
    
  } catch (error) {
    console.log('getCustomersWithWalletTxBetween error:', error);
    return [];
  }
}
